import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Input } from '../../../components/ui';
import {
  getApiBaseUrl,
  getServerBaseUrl,
  setApiBaseUrl,
  validateApiBaseUrl,
  probarConexionServidor,
} from '../../../services/apiConfig';

type ServerConfigPageProps = {
  bloqueoInicio?: boolean;
  mensajeInicial?: string;
};

const SERVIDORES_PREDEFINIDOS = [
  'https://pagina1.com',
  'https://pagina2.com',
  'http://192.168.1.50:8000',
];

function toEntradaServidor(apiBase: string): string {
  if (apiBase.endsWith('/api')) {
    return apiBase.slice(0, -4);
  }
  return apiBase;
}

export const ServerConfigPage: React.FC<ServerConfigPageProps> = ({
  bloqueoInicio = false,
  mensajeInicial = '',
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const servidorActual = useMemo(() => toEntradaServidor(getApiBaseUrl()), []);
  const [urlSeleccionada, setUrlSeleccionada] = useState(servidorActual);
  const [error, setError] = useState(mensajeInicial);
  const [guardando, setGuardando] = useState(false);
  const [comprobando, setComprobando] = useState(false);
  const [okMsg, setOkMsg] = useState('');

  const from = searchParams.get('from') || '';

  const seleccionarPredefinida = (url: string) => {
    setUrlSeleccionada(url);
    setError('');
    setOkMsg('');
  };

  const volver = () => {
    if (bloqueoInicio) return;
    if (from === 'app') {
      navigate('/', { replace: true });
      return;
    }
    navigate('/login', { replace: true });
  };

  const probarConexion = async () => {
    setError('');
    setOkMsg('');

    const validacion = validateApiBaseUrl(urlSeleccionada);
    if (!validacion.ok) {
      setError(validacion.error || 'La URL ingresada no es válida.');
      return;
    }

    setComprobando(true);
    try {
      const resultado = await probarConexionServidor(urlSeleccionada);
      if (!resultado.ok) {
        setError(resultado.error || 'No se pudo conectar con el servidor seleccionado');
        return;
      }
      setOkMsg('Conexión exitosa con el servidor.');
    } finally {
      setComprobando(false);
    }
  };

  const guardarYContinuar = async () => {
    setError('');
    setOkMsg('');

    const validacion = validateApiBaseUrl(urlSeleccionada);
    if (!validacion.ok || !validacion.normalized) {
      setError(validacion.error || 'La URL ingresada no es válida.');
      return;
    }

    setGuardando(true);
    try {
      const prueba = await probarConexionServidor(urlSeleccionada);
      if (!prueba.ok) {
        setError(prueba.error || 'No se pudo conectar con el servidor seleccionado');
        return;
      }

      setApiBaseUrl(urlSeleccionada);
      localStorage.removeItem('tiempos_auth_token');
      localStorage.removeItem('tiempos_auth_user');
      sessionStorage.setItem('tiempos_login_aviso', 'Servidor actualizado correctamente. Inicie sesión nuevamente.');
      navigate('/login', { replace: true });
    } catch (e: any) {
      setError(e?.message || 'No se pudo guardar la configuración del servidor.');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-emerald-100 p-5 sm:p-8 space-y-4">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Configurar servidor</h1>
        <p className="text-sm text-gray-600 mt-1">
          Seleccione o escriba la URL del backend antes de iniciar sesión.
        </p>
        <p className="text-xs text-gray-500 mt-2">
          Servidor actual: <span className="font-mono">{toEntradaServidor(getServerBaseUrl())}</span>
        </p>
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium text-gray-700">Servidores predefinidos</p>
        <div className="grid gap-2">
          {SERVIDORES_PREDEFINIDOS.map((url) => (
            <button
              key={url}
              type="button"
              onClick={() => seleccionarPredefinida(url)}
              className={`w-full text-left px-3 py-2 rounded-lg border text-sm transition ${
                urlSeleccionada.trim() === url
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-gray-200 hover:border-gray-300 text-gray-700'
              }`}
            >
              {url}
            </button>
          ))}
        </div>
      </div>

      <Input
        label="URL del servidor"
        type="text"
        value={urlSeleccionada}
        onChange={(e) => {
          setUrlSeleccionada(e.target.value);
          setError('');
          setOkMsg('');
        }}
        placeholder="https://mi-servidor.com"
        required
      />

      {error && (
        <div className="bg-danger-50 text-danger-600 px-4 py-3 rounded-lg text-sm">{error}</div>
      )}
      {okMsg && (
        <div className="bg-green-50 text-green-700 px-4 py-3 rounded-lg text-sm">{okMsg}</div>
      )}

      <div className="flex gap-2">
        <Button
          type="button"
          variant="secondary"
          className="flex-1"
          onClick={probarConexion}
          isLoading={comprobando}
          disabled={guardando}
        >
          Probar conexión
        </Button>
        <Button
          type="button"
          variant="primary"
          className="flex-1"
          onClick={guardarYContinuar}
          isLoading={guardando}
          disabled={comprobando}
        >
          Guardar y continuar
        </Button>
      </div>

      {!bloqueoInicio && (
        <button
          type="button"
          onClick={volver}
          className="w-full text-sm text-gray-500 hover:text-gray-700"
        >
          Cancelar
        </button>
      )}
    </div>
  );
};

export default ServerConfigPage;